/**
 * The top band: cash, lives, round, speed, settings.
 *
 * Readouts roll rather than snap — a number that jumps 200 in one frame reads
 * as a bug, a number that counts up reads as a reward. Cash flashes on gain,
 * lives shake on loss, and both effects collapse to a single frame under
 * `prefers-reduced-motion`.
 */

import { SIM } from '@/core/config';
import { trackBandHeight } from './Bands';
import { el, groupDigits, icon, installUiTheme, money, prefersReducedMotion, setIcon } from './Icon';
import type { IconName } from './Icon';

export interface HudState {
  cash: number;
  lives: number;
  round: number;
  totalRounds: number;
  /** Simulation speed multiplier, matched against `SIM.speeds`. */
  speed: number;
  paused: boolean;
}

export interface HudCallbacks {
  /** Cycle 1× → 2× → 3×. */
  onSpeed?(next: number): void;
  onTogglePause?(paused: boolean): void;
  onSettings?(): void;
}

/**
 * Lives at or under this pulse the HUD and fire the critical haptic. One
 * number, exported, because main.ts needs the same threshold and a drifted
 * copy would pulse the readout on a different life than the pattern fires on.
 */
export const LIVES_CRITICAL = 10;

/**
 * Eases a displayed number toward its target on the render clock.
 *
 * Large jumps travel fast and land exactly; small ones step by one so the last
 * digits tick audibly rather than blurring past.
 */
export class RollingNumber {
  private shown = 0;
  private target = 0;
  private raf = 0;
  private readonly instant: boolean;

  constructor(
    private readonly node: HTMLElement,
    private readonly format: (v: number) => string = groupDigits,
    initial = 0,
  ) {
    this.instant = prefersReducedMotion();
    this.shown = initial;
    this.target = initial;
    node.textContent = this.format(initial);
  }

  /**
   * Widest formatted value seen this run, in characters. The roll passes
   * through intermediate magnitudes (998 → 1,013 crosses 1,000), and letting
   * the pill widen mid-roll shoved every sibling readout sideways — uiprobe
   * grew a settle() helper just to wait it out. Reserving the target's width
   * up front means the pill resizes once, before the roll, never during it.
   */
  private widthCh = 0;

  private reserve(value: number): void {
    const ch = this.format(value).length;
    if (ch > this.widthCh) {
      this.widthCh = ch;
      this.node.style.minWidth = `${ch}ch`;
    }
  }

  /** Returns the signed change, so the caller can trigger a flash. */
  set(value: number): number {
    const delta = value - this.target;
    this.target = value;
    this.reserve(value);
    if (this.instant) {
      this.shown = value;
      this.node.textContent = this.format(value);
      return delta;
    }
    if (this.raf === 0) this.raf = requestAnimationFrame(this.tick);
    return delta;
  }

  /** Snaps without animating. Used when a run resets. */
  reset(value: number): void {
    this.target = value;
    this.shown = value;
    // A new run may never reach the old run's magnitude; let the pill shrink.
    this.widthCh = 0;
    this.node.style.minWidth = '';
    this.reserve(value);
    this.node.textContent = this.format(value);
  }

  private readonly tick = (): void => {
    const gap = this.target - this.shown;
    if (Math.abs(gap) < 1) {
      this.shown = this.target;
      this.raf = 0;
    } else {
      const step = gap * 0.24;
      this.shown += Math.abs(step) < 1 ? Math.sign(gap) : step;
      this.raf = requestAnimationFrame(this.tick);
    }
    this.node.textContent = this.format(Math.round(this.shown));
  };

  destroy(): void {
    if (this.raf !== 0) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }
}

/** Drops a class after its animation would have finished. */
function pulse(node: HTMLElement, cls: string, ms: number): void {
  node.classList.remove(cls);
  // Force a reflow so re-adding the class restarts the animation.
  void node.offsetWidth;
  node.classList.add(cls);
  window.setTimeout(() => node.classList.remove(cls), ms);
}

export class HUD {
  private root = el('div', 'hud-band hud-band--top');
  private cashBox = el('div', 'hud-stat hud-stat--cash');
  private livesBox = el('div', 'hud-stat hud-stat--lives');
  private roundBox = el('div', 'hud-stat hud-stat--round');
  private cashNum!: RollingNumber;
  private livesNum!: RollingNumber;
  private roundNum!: RollingNumber;
  private roundTotal = el('span', 'hud-stat__sub');
  private speedBtn = el('button', 'btn btn--icon');
  private speedIcon!: HTMLElement;
  private speedLabel = el('span', 'num');
  private pauseBtn = el('button', 'btn btn--icon');
  private pauseIcon!: HTMLElement;
  private settingsBtn = el('button', 'btn btn--icon');

  // `totalRounds` seeds impossible on purpose. Seeded at 100 it matched the
  // real value, the change guard in `update` never fired, and `/100` stayed an
  // empty span for the whole session while the wave bar 30px below showed it.
  private last: HudState = {
    cash: 0, lives: 0, round: 0, totalRounds: -1, speed: 1, paused: false,
  };
  private mounted = false;
  private untrack: (() => void) | null = null;

  constructor(private readonly cb: HudCallbacks = {}) {}

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    installUiTheme();

    const stats = el('div', 'hud-stats');

    // Cash — `coins`, the stack, because it is a running total.
    const cashVal = el('span', 'hud-stat__val num', '0');
    this.cashBox.append(
      icon('coins', { size: 16, role: 'uiCash', glow: 0.9, title: 'Cash' }),
      cashVal,
    );
    this.cashNum = new RollingNumber(cashVal, money);

    // Lives — `heart`, one mark, because it is a life bar.
    const livesVal = el('span', 'hud-stat__val num', '0');
    this.livesBox.append(
      icon('heart', { size: 16, role: 'uiLives', glow: 0.9, title: 'Lives' }),
      livesVal,
    );
    this.livesNum = new RollingNumber(livesVal);

    // Round — `flag-checkered`, the finish line, counted toward.
    const roundVal = el('span', 'hud-stat__val num', '0');
    this.roundBox.append(
      icon('flag-checkered', { size: 16, role: 'uiWave', glow: 0.8, title: 'Round' }),
      roundVal,
      this.roundTotal,
    );
    this.roundNum = new RollingNumber(roundVal);

    stats.append(this.cashBox, this.livesBox, this.roundBox);

    // Speed — `play` at 1×, `bolt` above it. Pause has its own control.
    this.speedIcon = icon('play', { size: 16, role: 'uiAccent', glow: 0.7 });
    this.speedLabel.textContent = '1×';
    this.speedBtn.append(this.speedIcon, this.speedLabel);
    this.speedBtn.type = 'button';
    this.speedBtn.setAttribute('aria-label', 'Game speed');
    this.speedBtn.dataset.tip = 'Game speed — cycles 1×, 2×, 3×. [Space]';
    this.speedBtn.addEventListener('click', this.onSpeed);

    this.pauseIcon = icon('pause', { size: 16, glow: 0.5 });
    this.pauseBtn.append(this.pauseIcon);
    this.pauseBtn.type = 'button';
    this.pauseBtn.setAttribute('aria-label', 'Pause');
    this.pauseBtn.dataset.tip = 'Pause the run. [Esc]';
    this.pauseBtn.addEventListener('click', this.onPause);

    this.settingsBtn.append(icon('cog', { size: 16, glow: 0.5 }));
    this.settingsBtn.type = 'button';
    this.settingsBtn.setAttribute('aria-label', 'Settings');
    this.settingsBtn.dataset.tip = 'Quality, audio and CRT settings.';
    this.settingsBtn.addEventListener('click', this.onSettings);

    const actions = el('div', 'hud-actions');
    actions.append(this.speedBtn, this.pauseBtn, this.settingsBtn);

    this.root.append(stats, actions);
    root.append(this.root);
    // In dock mode the playfield starts where this band ends, and the band is
    // 62px on a tablet and 68px on a 320px phone where the stats wrap.
    this.untrack = trackBandHeight(this.root, '--hud-h');
  }

  update(state: HudState): void {
    if (!this.mounted) return;
    const prev = this.last;

    const dCash = this.cashNum.set(state.cash);
    if (dCash > 0) pulse(this.cashBox, 'is-gain', 280);
    else if (dCash < 0) pulse(this.cashBox, 'is-spend', 220);

    const dLives = this.livesNum.set(state.lives);
    if (dLives < 0) pulse(this.livesBox, 'is-hit', 300);
    this.livesBox.classList.toggle('is-critical', state.lives > 0 && state.lives <= LIVES_CRITICAL);

    if (state.round !== prev.round) this.roundNum.set(state.round);
    if (state.totalRounds !== prev.totalRounds) {
      this.roundTotal.textContent = `/${state.totalRounds}`;
    }

    if (state.speed !== prev.speed) {
      this.speedLabel.textContent = `${state.speed}×`;
      setIcon(this.speedIcon, state.speed > 1 ? 'bolt' : 'play');
      this.speedIcon.style.setProperty('--pxi-glow', state.speed > 1 ? '1.1' : '0.7');
    }

    if (state.paused !== prev.paused) {
      const name: IconName = state.paused ? 'play' : 'pause';
      setIcon(this.pauseIcon, name);
      this.pauseBtn.setAttribute('aria-label', state.paused ? 'Resume' : 'Pause');
      this.pauseBtn.classList.toggle('btn--primary', state.paused);
    }

    this.last = { ...state };
  }

  /** Snaps every readout. Called when a new run starts. */
  resetTo(state: HudState): void {
    this.cashNum.reset(state.cash);
    this.livesNum.reset(state.lives);
    this.roundNum.reset(state.round);
    this.roundTotal.textContent = `/${state.totalRounds}`;
    this.last = { ...state };
  }

  destroy(): void {
    this.speedBtn.removeEventListener('click', this.onSpeed);
    this.pauseBtn.removeEventListener('click', this.onPause);
    this.settingsBtn.removeEventListener('click', this.onSettings);
    this.cashNum.destroy();
    this.livesNum.destroy();
    this.roundNum.destroy();
    this.untrack?.();
    this.untrack = null;
    this.root.remove();
    this.mounted = false;
  }

  private readonly onSpeed = (): void => {
    const speeds = SIM.speeds;
    const i = speeds.indexOf(this.last.speed as (typeof speeds)[number]);
    const next = speeds[(i + 1) % speeds.length];
    this.cb.onSpeed?.(next);
  };

  private readonly onPause = (): void => {
    this.cb.onTogglePause?.(!this.last.paused);
  };

  private readonly onSettings = (): void => {
    this.cb.onSettings?.();
  };
}
